import { createAsyncThunk } from '@reduxjs/toolkit';

import { setIsAppInitialized } from '@/app/actions';
import { getIsAppInitialized } from '@/app/store/selectors';
import { loadAllModels } from '@/modules/car/actions/async/load-all-models';
import { loadCarDocDefinitions } from '@/modules/car/actions/async/load-car-doc-definitions';
import { loadAllCars } from '@/modules/car/actions/async/loadAllCars';
import { reloadFormForCurrentCar } from '@/modules/car/actions/async/reload-form-for-current-car';
import { loadCities } from '@/modules/common/store/actions';
import { loadDriver } from '@/modules/driver/actions/async/load-driver';
import { loadDriverDocDefinitions } from '@/modules/driver/actions/async/load-driver-doc-definitions';
import { PartnerBlockedError } from '@/modules/partner/api';
import { namespace } from '@/modules/partner/namespace';
import { getCity, getPartnerType } from '@/modules/partner/store/selectors';
import { loadUser } from '@/modules/user/actions';
import { getRoles, ROLES, selectIsChauffeur, selectIsNew } from '@/modules/user/store/selectors';
import { RootState } from '@/root-store';
import { errorHandling } from '@/services/errorHandling';
import { LoginRequiredError } from '@/shared/api/fetch-based-http-methods';

import { InitialLoadError } from './errors';
import { loadPartnerDocDefinitions } from './loadDocDefinitions';
import { loadPartner } from './loadPartner';
import { loadReferralCode } from './loadReferralCode';
import { setEmptyPartner } from './setEmptyPartner';

const thunkName = `${namespace}/load-initial-data`;

const loadInitialData = createAsyncThunk(thunkName, async (_, { dispatch, getState }) => {
  try {
    const isAppInitialized = getIsAppInitialized(getState() as RootState);

    if (isAppInitialized) {
      return;
    }

    await Promise.all([dispatch(loadUser()).unwrap(), dispatch(loadCities()).unwrap()]);

    const isNewUser = selectIsNew(getState() as RootState);
    const isChauffeur = selectIsChauffeur(getState() as RootState);

    if (isNewUser || isChauffeur) {
      await dispatch(setEmptyPartner()).unwrap();
    } else {
      await dispatch(loadPartner()).unwrap();
    }

    if (!isNewUser) {
      const partnerType = getPartnerType(getState() as RootState);
      const userHasDriverRole = getRoles(getState() as RootState).includes(ROLES.DRIVER);

      await Promise.all([
        userHasDriverRole ? dispatch(loadReferralCode()).unwrap() : [],
        dispatch(loadDriver()).unwrap(),
        isChauffeur
          ? []
          : [
              dispatch(loadPartnerDocDefinitions(partnerType)).unwrap(),
              dispatch(loadCarDocDefinitions()).unwrap(),
              dispatch(loadDriverDocDefinitions()).unwrap(),
              dispatch(loadAllCars()).unwrap(),
            ],
      ]);
    }

    const city = getCity(getState() as RootState);

    await Promise.all([
      dispatch(loadAllModels(city)).unwrap(),
      dispatch(reloadFormForCurrentCar({})).unwrap(),
    ]);

    dispatch(setIsAppInitialized());
  } catch (e: any) {
    if (LoginRequiredError.is(e) || PartnerBlockedError.is(e)) {
      throw e;
    }

    errorHandling.captureException(e);
    throw new InitialLoadError(e?.message);
  }
});

export { loadInitialData };
